import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay';
import ScoreListItem from './components/ScoreListItem';
import ProgressBar from './components/ProgressBar';
import './NutritionLanding.scss';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchHealthSpanProfileAction } from '../../actions/healthSpanActions';
import { useScoreCategoryMetadata } from './useScoreCategoryMetadata';
import {
  HEALTH_SPAN_MAX_SCORE,
  SUB_CATEGORY_KEY_MAP,
  buildLandingScores,
  hasValidScore,
  getSubCategoryDisplayProps,
} from './healthSpanScore.utils';

const ASSESSMENT_IDS = {
  nutrition: process.env.REACT_APP_NUTRITION_ASSESSMENT_ID || '',
  water: process.env.REACT_APP_WATER_ASSESSMENT_ID || '',
  sleep: process.env.REACT_APP_SLEEP_ASSESSMENT_ID || '',
};

const ROUTE_BY_KEY = {
  behavior: commonConstant.pathFoodLogHome,
};

const CATEGORIES = [
  {
    key: 'nutrition',
    image: assessmentImage.nutritionImage,
    name: 'คุณภาพโภชนาการ',
  },
  {
    key: 'water',
    image: assessmentImage.waterImage,
    name: 'การดื่มน้ำ',
  },
  {
    key: 'sleep',
    image: assessmentImage.sleepImage,
    name: 'การนอนหลับ',
  },
  {
    key: 'behavior',
    image: assessmentImage.behaviorImage,
    name: 'พฤติกรรมสุขภาพ',
  },
];

const NutritionLanding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.healthSpan);
  const { metadata, metaLoading } = useScoreCategoryMetadata('nutrition');

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  const { categoryScore, categoryStatus, scores } = buildLandingScores(
    profile,
    'nutrition',
    CATEGORIES,
    metadata
  );

  const hasValue = hasValidScore(categoryScore);
  const isLoading = loading || metaLoading;
  const tip = categoryStatus?.tip;
  const noDataColor = '#A1A1A1';
  const displayColor = hasValue ? categoryStatus.barColor : noDataColor;

  const handleCategoryClick = (category) => {
    const directRoute = ROUTE_BY_KEY[category.key];
    if (directRoute) {
      history.push(directRoute);
      return;
    }

    const assessmentId = ASSESSMENT_IDS[category.key];
    if (!assessmentId) return;

    history.push(
      `${commonConstant.pathAssessmentQuestion}?assessment_id=${assessmentId}&return=${commonConstant.pathNutritionAssessmentLanding}&section=${category.key}`
    );
  };

  const headerScore = hasValue ? categoryScore : '-';
  const headerMaxScore = categoryStatus?.maxScore || HEALTH_SPAN_MAX_SCORE;

  return (
    <div className="nutrition-landing-component">
      {isLoading && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {!isLoading && (
      <>
      <div className="bg-detail-container">
        <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
      </div>

      <div className="content-container">
        <div className="header-section">
          <div className="image-component">
            <img src={assessmentImage.nutritionImage} alt="" />
          </div>
          <div className="detail" style={{ color: displayColor }}>
            <div className="header-name">{metadata?.display_name || 'โภชนาการและการฟื้นฟู'}</div>
            <div className="status">{hasValue ? categoryStatus.statusText : 'ไม่มีข้อมูล'}</div>
            <div className="score-container">
              <div className="value">{headerScore}</div>
              <div className="max-value">/{headerMaxScore}</div>
            </div>
            <ProgressBar
              value={hasValue ? categoryScore : 0}
              maxValue={headerMaxScore}
              width="130px"
              barColor={displayColor}
            />
          </div>
        </div>

        {hasValue && tip && (
          <div className="tip-card">
            <div className="tip-label-container">
              <img src={assessmentImage.tipIcon} alt="" className="tip-label-image" />
              <div className="tip-label-text">คำแนะนำ</div>
            </div>
            <div className="tip-detail-container">
              <img src={tip.image || assessmentImage.vetgetableImage} alt="" className="image" />
              <div className="text-container">
                <div className="tip-text">{tip.title}</div>
                <div className="tip-detail-text">{tip.description}</div>
              </div>
            </div>
          </div>
        )}

        <div className="summary-score-list">
          <div className="title">ข้อมูลโภชนาการ</div>

          {CATEGORIES.map((category) => {
            const scoreData = scores[category.key] || {};
            const hasData = hasValidScore(scoreData.score);
            const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
            const subName =
              metadata?.sub_categories?.[apiKey]?.display_name || category.name;
            const display = getSubCategoryDisplayProps(scoreData, hasData);

            return (
              <ScoreListItem
                key={category.key}
                image={category.image}
                name={subName}
                statusText={display.statusText}
                statusColor={display.barColor}
                value={hasData ? scoreData.score : '-'}
                maxValue={scoreData.maxScore || 0}
                barColor={display.barColor}
                valueColor={display.valueColor}
                hasData={hasData}
                onClick={() => handleCategoryClick(category)}
              />
            );
          })}
        </div>

        <div className="description-card">
          <div className="title">โภชนาการและร่างกาย (Nutrition & Body)</div>
          <div className="description-text">
            <span className="highlight">ประเมินพฤติกรรมการรับประทานอาหาร</span>
            ความสมดุลของสารอาหาร และองค์ประกอบของร่างกาย เพื่อสนับสนุนสุขภาพและการใช้ชีวิตที่แข็งแรง
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default NutritionLanding;
